import { useState } from "react";

import { Check, FileCheck2 } from "lucide-react";

import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { ConfirmationModal } from "@components/ConfirmationModal";

type Props = {
  open: boolean;
  isDownloading: boolean;
  onOpenChange: (open: boolean) => void;
  /** Must return a Promise so `submitting` can be reset in `.finally()`. */
  onConfirm: (password?: string) => Promise<void>;
};

export const DownloadPdfModal = ({
  open,
  isDownloading,
  onOpenChange,
  onConfirm,
}: Props) => {
  const [aiDisclaimerChecked, setAiDisclaimerChecked] = useState(false);
  const [protectWithPassword, setProtectWithPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const resetState = () => {
    setAiDisclaimerChecked(false);
    setProtectWithPassword(false);
    setPassword("");
    setPasswordConfirm("");
    setSubmitting(false);
  };

  const passwordMismatch =
    protectWithPassword && (password.length === 0 || password !== passwordConfirm);

  const confirmDisabled = !aiDisclaimerChecked || passwordMismatch;
  const busy = submitting || isDownloading;

  const handleConfirm = async () => {
    setSubmitting(true);
    const submittedPassword = protectWithPassword ? password : undefined;
    try {
      await onConfirm(submittedPassword);
      // Download kicked off successfully — close the modal.
      onOpenChange(false);
      resetState();
    } catch {
      // onConfirm surfaces its own error toast; keep the modal open so the
      // user can retry without reopening it.
      setSubmitting(false);
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) resetState();
    onOpenChange(nextOpen);
  };

  const confirmPasswordError =
    passwordConfirm.length > 0 && password !== passwordConfirm;

  return (
    <ConfirmationModal
      open={open}
      isLoading={busy}
      icon={<FileCheck2 className="size-10 text-primary" />}
      title="Download Report as PDF"
      description=""
      body={
        <div className="space-y-4 text-left">
          <p className="text-sm text-foreground-muted">
            Before downloading the final report, please confirm you have completed
            the following:
          </p>

          <ul className="space-y-2">
            {[
              "Reviewed all sections of this report thoroughly.",
              "Added my medical opinion to all relevant sections.",
              "Selected the applicable expert witness code(s) of conduct.",
            ].map((item) => (
              <li key={item} className="flex items-start gap-2">
                <Check className="mt-0.5 size-4 shrink-0 text-success" />
                <span className="text-sm text-foreground-muted">{item}</span>
              </li>
            ))}
          </ul>

          {/* AI disclaimer — div wrapper because Radix Checkbox renders a
              <button>, which is not a labelable element. */}
          <div className="flex cursor-pointer items-start gap-3 rounded-lg border border-primary/30 bg-primary-50 p-4">
            <Checkbox
              id="pdf-dl-ai-disclaimer"
              checked={aiDisclaimerChecked}
              onCheckedChange={(checked) => setAiDisclaimerChecked(checked === true)}
              className="mt-0.5"
            />
            <Label
              htmlFor="pdf-dl-ai-disclaimer"
              className="cursor-pointer text-sm font-normal leading-relaxed"
            >
              I understand that Report Studio may provide summaries that could
              contain errors. I confirm that I have reviewed and corrected any
              content I have relied upon, and I accept responsibility for ensuring
              the final report accurately reflects my professional opinion.
            </Label>
          </div>

          <div className="space-y-3 rounded-lg border border-border p-4">
            <div className="flex cursor-pointer items-start gap-3">
              <Checkbox
                id="pdf-dl-protect-password"
                checked={protectWithPassword}
                onCheckedChange={(checked) =>
                  setProtectWithPassword(checked === true)
                }
                className="mt-0.5"
              />
              <Label
                htmlFor="pdf-dl-protect-password"
                className="cursor-pointer text-sm font-medium"
              >
                Protect this PDF with a password
              </Label>
            </div>

            {protectWithPassword && (
              <div className="space-y-2 pl-7">
                <div className="space-y-1">
                  <Label htmlFor="pdf-dl-password">Password</Label>
                  <Input
                    id="pdf-dl-password"
                    type="password"
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="pdf-dl-password-confirm">Confirm password</Label>
                  <Input
                    id="pdf-dl-password-confirm"
                    type="password"
                    autoComplete="new-password"
                    value={passwordConfirm}
                    onChange={(e) => setPasswordConfirm(e.target.value)}
                    aria-invalid={confirmPasswordError}
                    aria-describedby={
                      confirmPasswordError
                        ? "pdf-dl-password-confirm-error"
                        : undefined
                    }
                  />
                  {confirmPasswordError && (
                    <p
                      id="pdf-dl-password-confirm-error"
                      className="text-xs text-destructive"
                    >
                      Passwords do not match.
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      }
      confirmButtonText="Download PDF"
      confirmDisabled={confirmDisabled || busy}
      cancelButtonText="Go back"
      onConfirm={handleConfirm}
      onOpenChange={handleOpenChange}
    />
  );
};
